import { Clock, Star } from 'lucide-react';
import type { Task } from '@/types/taskTypes';
import { Button } from './ui/button';

interface TaskShortcutsProps {
  tasks: Task[];
  favoriteIds: string[];
  recentIds: string[];
  isPremium: boolean;
  onSelect: (task: Task) => void;
  onToggleFavorite: (taskId: string) => void;
  onBrowse: () => void;
}

export function TaskShortcuts({
  tasks,
  favoriteIds,
  recentIds,
  isPremium,
  onSelect,
  onToggleFavorite,
  onBrowse,
}: TaskShortcutsProps) {
  const favorites = favoriteIds
    .map(taskId => tasks.find(task => task.id === taskId))
    .filter((task): task is Task => !!task);
  const recent = [...new Set(recentIds)]
    .map(taskId => tasks.find(task => task.id === taskId || task.task_type === taskId))
    .filter((task): task is Task => !!task)
    .filter((task, index, items) => items.findIndex(item => item.id === task.id) === index)
    .slice(0, 6);
  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Your task shortcuts</h2>
          <p className="mt-1 text-sm text-slate-600">
            Pick up a recent task or save your frequent workflows.
          </p>
        </div>
        <Button variant="outline" onClick={onBrowse}>
          Browse all tasks
        </Button>
      </div>
      {[
        { title: 'Favorites', icon: Star, items: favorites },
        { title: 'Recent tasks', icon: Clock, items: recent },
      ].map(section => (
        <section key={section.title} className="mb-8">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <section.icon className="h-4 w-4" />
            {section.title}
          </h3>
          {section.items.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-500">
              {section.title === 'Favorites'
                ? 'Use the star beside a task in the list to keep it here.'
                : 'Tasks you open or run appear here.'}
            </p>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {section.items.map(task => (
                <div
                  key={task.id}
                  className="relative rounded-lg border border-slate-200 bg-white hover:border-blue-300"
                >
                  <button
                    onClick={() => onSelect(task)}
                    disabled={!!task.premium_only && !isPremium}
                    className="h-full w-full rounded-lg p-4 pr-12 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 disabled:opacity-60"
                  >
                    <span className="block text-sm font-medium text-slate-900">{task.name}</span>
                    <span className="mt-1 block text-xs leading-5 text-slate-500">
                      {task.description}
                    </span>
                    {task.premium_only && !isPremium && (
                      <span className="mt-2 block text-xs text-amber-700">Premium required</span>
                    )}
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-2 h-8 w-8"
                    aria-label={`${favoriteIds.includes(task.id) ? 'Remove' : 'Add'} ${task.name} ${favoriteIds.includes(task.id) ? 'from' : 'to'} favorites`}
                    aria-pressed={favoriteIds.includes(task.id)}
                    onClick={() => onToggleFavorite(task.id)}
                  >
                    <Star
                      className={`h-4 w-4 ${favoriteIds.includes(task.id) ? 'fill-amber-400 text-amber-500' : 'text-slate-400'}`}
                    />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
